/* eslint-disable react/react-in-jsx-scope */
import { useAuth } from "@/auth/hooks/useAuth";
import ReactBreadcrumb from "@/components/breadcrumb/Breadcrumb";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { listAllMonths } from "@/constants/constants";
import { FundingSkeleton } from "@/dashboard/components/fundings/FundingByProject";
import NewFundingByFunder from "@/dashboard/components/fundings/new_fundings/NewFundingByFunder";
import NewFundingByProject from "@/dashboard/components/fundings/new_fundings/NewFundingByProject";
// import NewFundingByFunder from "@/dashboard/components/fundings/new_fundings_by_funder/NewFundingByFunder";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { autoScrollJoyrideStep } from "@/libs/joyrideAutoScroll";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Joyride, { CallBackProps, STATUS, Step } from "react-joyride";
import { useSearchParams } from "react-router-dom";

const Funding = () => {
  const { t } = useTranslation("dashboard", {
    keyPrefix: "financeur",
  });

  const [checked, setChecked] = useState(false); // false = 'project', true = financeur
  const launchDate = 2025;
  const [searchParams, setSearchParams] = useSearchParams();
  const yearParam = searchParams.get("year");
  const [year, setYear] = useState(
    yearParam ? parseInt(yearParam) : launchDate
  );
  const [loading, setLoading] = useState(false);
  //a changer
  const expectedDurationYear = 24;
  const arrYear = Array.from(
    { length: expectedDurationYear },
    (_, index) => index + 1
  );

  const handleYearChange = (action: "prev" | "next") => {
    const currentYear = action === "prev" ? year - 1 : year + 1;
    setSearchParams({ year: currentYear.toString() });
  };

  const handleSelectYear = (value: string) => {
    const currentYear = Number(value);
    setSearchParams({ year: currentYear.toString() });
  };

  useEffect(() => {
    const yearParam = searchParams.get("year");
    if (yearParam) {
      setYear(parseInt(yearParam));
    }
  }, [searchParams]);

  useEffect(() => {
    setLoading(true);
    const timeout = setTimeout(() => {
      setLoading(false);
    }, 300);

    return () => clearTimeout(timeout);
  }, [checked]);
  const { user } = useAuth();

  const steps: Step[] = [
    {
      target: "body", // Étape de bienvenue sans ciblage spécifique
      content: (
        <div className="text-center">
          <h2 className="text-xl font-bold mb-4">
            {t("Bienvenue sur la page demande de financements !")}
          </h2>
          <p>
            {t(
              "Explorez les fonctionnalités de gestion des demandes de financement pour suivre, ajouter, et modifier vos financements en toute simplicité."
            )}
          </p>
        </div>
      ),
      placement: "center",
      disableBeacon: true,
      spotlightClicks: true,
    },
    {
      target: "#switch_view",
      title: t("Choix de l'affichage"),
      content: (
        <div className="text-center">
          <p>{t("Choisissez votre mode d'affichage selon vos besoins.")}</p>
        </div>
      ),
      placement: "right",
      disableBeacon: true,
      spotlightClicks: true,
    },
    {
      target: "#table_fund",
      title: t("Récapitulatif mensuel des versements"),
      content: (
        <div className="text-center">
          <p>
            {t(
              "Consultez le récapitulatif mensuel des versements estimés et obtenus."
            )}
          </p>
        </div>
      ),
      placement: "top",
      disableBeacon: true,
      spotlightClicks: true,
    },
    {
      target: "#table_year",
      title: t("Filtrer par année"),
      content: (
        <div className="text-center">
          <p>
            {t(
              "Sélectionnez l’année pour voir les versements de cette période."
            )}
          </p>
        </div>
      ),
      placement: "left",
      disableBeacon: true,
      spotlightClicks: true,
    },
    {
      target: "#table_devise",
      title: t("Conversion de devises"),
      content: (
        <div className="text-center">
          <p>{t("Convertissez les montants dans la devise de votre choix.")}</p>
        </div>
      ),
      placement: "left",
      disableBeacon: true,
      spotlightClicks: true,
    },
    {
      target: "#funding",
      title: t("Consulter vos financements"),
      content: (
        <div className="text-center">
          <p>
            {t(
              "Consultez ici vos financements en cours avec un récapitulatif des versements."
            )}
          </p>
        </div>
      ),
      placement: "top",
      disableBeacon: true,
      spotlightClicks: true,
    },
    {
      target: "#add_funding",
      title: t("Ajouter un financement"),
      content: (
        <div className="text-center">
          <p>{t("Cliquez ici pour ajouter un nouveau financement.")}</p>
        </div>
      ),
      placement: "bottom",
      disableBeacon: true,
      spotlightClicks: true,
    },
  ];
  const [run, setRun] = useState(false);

  useEffect(() => {
    // Tutoriel legacy désactivé : le tutoriel pas-à-pas est maintenant géré
    // directement dans les vues partagées de suivi de financement.
    setRun(false);
  }, [user]);

  const handleJoyrideCallback = (data: CallBackProps) => {
    autoScrollJoyrideStep(data);
    const { status, action } = data;
    const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];

    if (finishedStatuses.includes(status) || action === "skip") {
      setRun(false);
      localStorage.setItem("hasSeenTutorialFunding", "true"); // Enregistre que l'utilisateur a terminé le tutoriel
    }
  };
  return (
    <>
      <Joyride
        steps={steps}
        run={run}
        callback={handleJoyrideCallback}
        continuous={false}
        scrollToFirstStep={false}
        disableScrolling={true}
        showProgress={true}
        showSkipButton={true}
        spotlightClicks={false}
        locale={{
          back: t("Précédent"),
          close: t("Suivant"),
          last: t("Terminer"),
          next: t("Suivant"),
          skip: t("Passer"),
        }}
        styles={{
          options: {
            arrowColor: "#fff",
            backgroundColor: "  #383937",
            overlayColor: "rgba(255, 255, 255, 0.8)",
            spotlightShadow: "0 0 0 rgba(0, 0, 0, 0)",
            textColor: "#fff",
            zIndex: 1000,
          },
          buttonBack: {
            backgroundColor: " #669523",
            color: "white",
            borderRadius: "20px",
            marginRight: "20px",
            width: "100px",
            fontSize: "15px",
          },

          buttonNext: {
            backgroundColor: "#669523",
            color: "white",
            borderRadius: "20px",
            width: "80px",
            fontSize: "15px",
          },
        }}
      />
      <div>
        <ReactBreadcrumb
          items={[
            {
              href: ``,
              name: t("Demande de financements"),
            },
          ]}
          isTruncated={false}
        />

        <div className="flex flex-wrap gap-5 items-start justify-between mt-8 ">
          <h1
            className="mb-2 text-[24px] font-medium inline-block"
            id="program-title"
          >
            {t("Demande de financements")}
          </h1>
          {/* <div className="flex flex-col items-end">
          <Link
            to="/dashboard/ajout-financeur"
            className={buttonVariants({ variant: "default" })}
          >
            {t("Ajouter un financeur")}
          </Link>
        </div> */}
        </div>
        <div className="financement flex flex-col  mt-3">
          <p>{t("Financement par")} :</p>
          <div className="flex items-center space-x-3">
            <Label>{t("Projet")}</Label>
            <Switch
              id="airplane-mode"
              className="data-[state=checked]:bg-input"
              thumbClassName="bg-secondary"
              checked={checked}
              onCheckedChange={setChecked}
            />
            <Label>{t("Financeurs")}</Label>
          </div>
          <div
            className="sticky top-0 bg-background pb-3 z-20"
            data-tour-funding-page-timeline-header="true"
          >
            <div className="flex items-center gap-3 justify-end mt-8">
              <div id="table_year" className="flex flex-row">
                <div className="flex gap-1">
                  <Button
                    onClick={() => handleYearChange("prev")}
                    className="bg-background flex size-8 items-center justify-center rounded-full p-0 transition-all hover:bg-gray-100"
                    disabled={launchDate == (Number(yearParam) || year)}
                  >
                    <ChevronLeft className="text-foreground/70" />
                  </Button>
                  <Button
                    onClick={() => handleYearChange("next")}
                    className="bg-background flex size-8 items-center justify-center rounded-full p-0 transition-all hover:bg-gray-100"
                    disabled={
                      launchDate - 1 + expectedDurationYear <=
                      (Number(yearParam) || year)
                    }
                  >
                    <ChevronRight className="text-foreground/70" />
                  </Button>
                </div>
                <Select
                  value={year.toString()}
                  onValueChange={handleSelectYear}
                >
                  <SelectTrigger className="text-foreground/90 w-16 border-0 px-0 text-lg outline-none focus:ring-0 focus:ring-transparent">
                    <SelectValue placeholder={year} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {arrYear.map((i, k) => (
                        <SelectItem value={"" + (launchDate + k)} key={k}>
                          {launchDate + k}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="relative ml-auto mt-3 flex flex-wrap items-center gap-2 w-full">
              <div className="flex w-5/6 gap-2 ml-auto pl-4 pr-6">
                {listAllMonths.map((i, k) => (
                  <div
                    className={`border-foreground/5 flex-1 border-x text-center `}
                    key={k}
                  >
                    {t(i)}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* false = 'project', true = financeur */}
          {loading ? (
            <>
              <div className="flex flex-row gap-4 justify-end items-center">
                <div className="flex py-3">
                  <ChevronLeft className="text-foreground/70" />
                  <ChevronRight className="text-foreground/70" />
                </div>
                <Skeleton className="w-[100px] h-4 rounded-[20px]" />
              </div>
              <div className=" relative ml-[180px] flex flex-row gap-2 mr-8">
                {listAllMonths.map((i, k) => (
                  <div
                    className={`border-foreground/5 flex-1 border-x text-center `}
                    key={k}
                  >
                    {t(i)}
                  </div>
                ))}
              </div>
              <div>
                <FundingSkeleton />
                <FundingSkeleton />
              </div>
            </>
          ) : !checked ? (
            <>
              <NewFundingByProject year={year} />
              {/* <FundingByProject year={year} /> */}
            </>
          ) : (
            // <FundingByProject year={year} />
            <NewFundingByFunder year={year} />
          )}
        </div>
      </div>
    </>
  );
};

export default Funding;
